import * as yup from "yup";

import type { Currency } from "@krea/shared/types/common";

import { AmortizationMethodEnum, BidTypeEnum } from "@/features/common/enums";
import type { ICompanyLoanBidInput } from "@/features/lender-responses/types/lender-response";

import { AmountValidation } from "../utils/validators/AmountValidation";
import { LenderMonthlyAmountValidation } from "../utils/validators/LenderMonthlyAmountValidation";
import { LenderMonthlyInterestValidation } from "../utils/validators/LenderMonthlyInterestValidation";
import { PaybackTimeMonthsValidation } from "../utils/validators/PaybackTimeMonthsValidation";
import { SetupFeeValidation } from "../utils/validators/SetupFeeValidation";
import { TotalCostValidation } from "../utils/validators/TotalCostValidation";

export const companyLoanBidSchema: (
  currency: Currency,
  formatWithCurrency: (value: number) => string,
) => yup.ObjectSchema<ICompanyLoanBidInput> = (currency, formatWithCurrency) =>
  yup.object().shape({
    type: yup
      .string()
      .required("Type is required")
      .oneOf([BidTypeEnum.COMPANY_LOAN])
      .default(BidTypeEnum.COMPANY_LOAN),
    amount: AmountValidation(currency, formatWithCurrency),
    amortizationMethod: yup
      .string()
      .oneOf(Object.values(AmortizationMethodEnum))
      .required("Amortization method is required")
      .default(AmortizationMethodEnum.ANNUITY),
    paybackTimeMonths: PaybackTimeMonthsValidation().max(
      120,
      (schema) => `Repayment time can't be more than ${schema.max} months`,
    ),
    lenderMonthlyInterest: LenderMonthlyInterestValidation({
      isFunction: (amortizationMethod: AmortizationMethodEnum) =>
        amortizationMethod !== AmortizationMethodEnum.FIXED,
    }),
    lenderMonthlyAmount: LenderMonthlyAmountValidation(
      currency,
      formatWithCurrency,
    ),
    totalCost: TotalCostValidation(formatWithCurrency),
    earlyRepaymentFee: yup.string().nullable(),
    postalFee: yup.number().nullable().min(0, "Should be at least 0"),
    directDebitFee: yup.number().nullable().min(0, "Should be at least 0"),
    setupFee: SetupFeeValidation(formatWithCurrency),
    monthlyCost: yup.number().nullable().min(0, "Should be at least 0"),
    monthlyCostWithoutSetupFee: yup
      .number()
      .nullable()
      .min(0, "Should be at least 0"),
  });

export const companyLoanBidDefaultValues: ICompanyLoanBidInput = {
  type: BidTypeEnum.COMPANY_LOAN,
  amortizationMethod: AmortizationMethodEnum.ANNUITY,
  amount: undefined,
  directDebitFee: undefined,
  earlyRepaymentFee: undefined,
  lenderMonthlyInterest: undefined,
  paybackTimeMonths: undefined,
  postalFee: undefined,
  setupFee: undefined,
  totalCost: undefined,
};
